import { Component } from '@theme/component';

/**
 * Last non-empty selling plan seen, held at module scope rather than on the
 * instance. Morphing the variant picker can replace this element with a fresh
 * one, and per-instance state would be lost exactly when it is needed — the
 * moment after a variant change.
 *
 * @type {string}
 */
let lastKnownPlan = '';

/**
 * The one rule deciding which purchase option's figures are visible.
 *
 * Rewritten in place rather than supplemented: `display: none` from one rule
 * cannot be undone by another, so a default rule plus an override rule would
 * hide every figure instead of swapping which one shows.
 *
 * Liquid renders this element (outside the variant picker, so morphing the
 * picker leaves it alone) with the one-time price as the default. It is only
 * created here as a fallback for markup that predates it.
 *
 * @returns {HTMLStyleElement}
 */
function planRuleElement() {
  const existing = document.querySelector('style[data-emberk-plan-rule]');
  if (existing instanceof HTMLStyleElement) return existing;

  const created = document.createElement('style');
  created.dataset.emberkPlanRule = '';
  document.head.append(created);

  return created;
}

/**
 * Keeps the Emberk variant picker's pricing figures in sync with the selected
 * purchase option (one-time vs. a subscription selling plan).
 *
 * Every set of figures for every selling plan is already in the DOM, rendered
 * by `snippets/emberk-variant-card-figures.liquid`. All this does is point one
 * CSS rule at the chosen plan, so money is never formatted in JavaScript and
 * currency and locale formatting stay in Liquid, where they are correct by
 * construction.
 *
 * ## Why a stylesheet rule rather than toggling elements
 *
 * Selecting a variant makes Horizon re-render the picker from the server, which
 * always marks the one-time figures as the visible ones. Anything applied to
 * those elements directly is therefore undone on every variant change and has
 * to be re-applied afterwards — and the browser paints in between, which reads
 * as the price flashing to one-time and back. A rule in a stylesheet outside
 * the re-rendered markup applies to the new figures the instant they land, with
 * no JavaScript in the critical path. It also degrades correctly: with
 * JavaScript disabled the Liquid-side rule shows the one-time price.
 *
 * ## Why this polls
 *
 * Horizon syncs components through events — `StandardEvents.productSelect`,
 * `ThemeEvents.quantitySelectorUpdate` and friends — and that is the pattern to
 * follow whenever an event exists. For selling plans, none does: Horizon ships
 * no subscription UI (`quick-add` routes products with selling plans to the
 * product page rather than handling them inline), and Shopify's standard events
 * cover cart, collection, product select/view and search, but not purchase
 * options.
 *
 * Subscriptions therefore come from an app, which owns that state privately and
 * writes the chosen plan into a hidden `selling_plan` field in the product form.
 * Assigning `input.value` fires no event and mutates no attribute, so there is
 * nothing to listen for or observe — and where the app renders its selector in
 * an iframe, even document-level capture never sees the interaction.
 *
 * Reading one hidden field on a timer is the only reliable signal available.
 * The work is a single `querySelector` and a string comparison, and it pauses
 * whenever the tab is in the background.
 *
 * @extends {Component}
 */
export default class EmberkVariantPricing extends Component {
  /**
   * Bumped whenever this file changes, and mirrored onto `data-emberk-version`,
   * so it is possible to confirm from the console which build is live rather
   * than guessing whether a copy-paste landed.
   */
  static VERSION = 'css-4-loop-strike-scope';

  /** Fast enough to feel immediate, slow enough to be free. */
  static #POLL_MS = 150;

  /**
   * Selling plan id currently applied, or an empty string for a one-time purchase.
   * @type {string}
   */
  #activePlan = '';

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    this.dataset.emberkVersion = EmberkVariantPricing.VERSION;

    this.#sync();
    this.#timer = setInterval(() => {
      if (document.hidden) return;
      this.#sync();
    }, EmberkVariantPricing.#POLL_MS);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
  }

  /**
   * Runs after a variant change re-renders the picker. The server always sends
   * the one-time figures visible, so the active plan has to be re-applied.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#apply();
  }

  /**
   * Re-reads the selected plan and re-asserts it.
   *
   * Deliberately applies every tick rather than only when the plan changes.
   * Selecting a variant re-renders the picker from the server with the one-time
   * figures visible, and that reset happens without the plan changing — so a
   * "only if it changed" guard would leave the wrong price on screen. Applying
   * is idempotent and costs one `querySelectorAll` over a handful of spans.
   */
  #sync() {
    this.#activePlan = this.#readSelectedPlan();
    this.#apply();
    this.#correctLoopStrikePrice();
  }

  /**
   * Makes Loop's struck-through price agree with the pack picker's savings.
   *
   * Loop's widget strikes the pack's ONE-TIME price beside the subscription
   * price: `LOOP_WIDGET_USE_COMPARE_AT_PRICE = false` is a constant inside
   * its script's closure, with no preference behind it, so neither the admin
   * nor CSS can change it. The picker measures savings against the pack's
   * compare-at price (the list price the store sets), so the two disagreed:
   * "Save 44%" next to a strike that implied 20%.
   *
   * Rather than lower the savings to match, the strike is rewritten with the
   * compare-at price, which Liquid formats onto each pack's radio as
   * `data-compare-at`. Only packs that have one are touched; Loop's own figure
   * stays otherwise. Runs on the same tick as the plan sync because Loop
   * re-renders its widget on every variant change and publishes no event.
   *
   * Writes are guarded by a text comparison so an unchanged tick touches
   * nothing.
   */
  #correctLoopStrikePrice() {
    // This element is an empty sibling of the cards, not their ancestor (see
    // the snippet: a wrapper would steal the variant picker's refs), so the
    // checked pack has to be looked up from the picker, not from `this`.
    const picker = this.closest('variant-picker') ?? this.closest('.shopify-section');
    const checked = picker?.querySelector('input[data-variant-id]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    const compareAt = checked.dataset.compareAt;
    if (!compareAt) return;

    const scope = this.closest('.shopify-section') ?? document;
    const strikes = scope.querySelectorAll(
      '.loop-widget-purchase-option-compare-at-price, .loop-w-btn-group-purchase-option-compare-at-price'
    );

    for (const strike of strikes) {
      if (!(strike instanceof HTMLElement)) continue;
      if (strike.textContent?.trim() !== compareAt) strike.textContent = compareAt;

      // Loop hides the element on the one-time option because its own figure
      // would equal the price there. With a real compare-at it is worth
      // showing, and it matches the picker's one-time "Save N%".
      strike.classList.remove('loop-display-none');
    }
  }

  /**
   * Reads the selected selling plan from the add-to-cart form.
   *
   * Scoping to the form matters. A Loop Subscriptions page carries more than
   * one `selling_plan` field — the app keeps its own, and a sticky add-to-cart
   * bar duplicates the form — and only the one inside the add-to-cart form
   * tracks the shopper's choice. The others can sit pinned on a subscription
   * plan, which reads as "always subscribed" if picked up by mistake.
   *
   * The form's field is also the authoritative one by definition: it is what
   * gets submitted, so matching it keeps the displayed price honest.
   *
   * @returns {string}
   */
  #readSelectedPlan() {
    const form =
      this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]') ??
      document.querySelector('form[action*="/cart/add"]');

    if (!form) return '';

    const fields = /** @type {(HTMLInputElement | HTMLSelectElement)[]} */ ([
      ...form.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]'),
    ]);

    // A control the shopper touched directly wins over an app-managed field.
    const checked = fields.find((field) => field instanceof HTMLInputElement && field.checked);
    const select = fields.find((field) => field instanceof HTMLSelectElement);
    const field = checked ?? select ?? fields[0];
    const value = field?.value || '';

    if (value) {
      lastKnownPlan = value;
      return value;
    }

    // An empty field does not always mean "one-time". Changing variant
    // re-renders the section, which can clear the field while Loop's widget
    // still shows a subscription selected. Reading that literally would drop
    // the price to one-time behind the shopper's back, so when Loop still says
    // subscription, restore the plan they actually picked.
    if (this.#loopSubscriptionSelected() && lastKnownPlan) return lastKnownPlan;

    return '';
  }

  /**
   * Whether Loop's widget currently shows a subscription rather than one-time.
   * Loop marks the chosen option with `--selected`, and only the subscription
   * option carries a selling plan group.
   *
   * @returns {boolean}
   */
  #loopSubscriptionSelected() {
    return Boolean(
      document.querySelector('.loop-widget-purchase-option-selected[data-loop-widget-selling-plan-group]')
    );
  }

  /**
   * Points the stylesheet rule at the active plan.
   *
   * Writing one CSS rule instead of toggling attributes on each figure is what
   * keeps the price steady when a variant changes. The server re-renders those
   * figures on every click, so anything applied to them directly is undone and
   * has to be re-applied after the fact — which the browser paints in between,
   * as a flash of the one-time price. The rule lives in a stylesheet outside
   * the re-rendered markup, so newly rendered figures match it immediately.
   */
  #apply() {
    const picker = this.closest('variant-picker');
    if (!picker) return;

    let plan = this.#activePlan;

    // A plan can be missing for a card if the merchant limited the subscription
    // to some variants. Falling back to the one-time price beats hiding
    // everything and leaving a card with no prices at all.
    if (plan && !picker.querySelector(`[data-emberk-plan="${CSS.escape(plan)}"]`)) {
      plan = '';
    }

    // Mirrored for debugging: compare against the form's hidden field.
    this.dataset.activePlan = plan;

    const rule = `.emberk-variant-picker [data-emberk-plan]:not([data-emberk-plan="${CSS.escape(
      plan
    )}"]) { display: none; }`;

    const element = planRuleElement();
    if (element.textContent === rule) return;

    element.textContent = rule;
  }
}

if (!customElements.get('emberk-variant-pricing')) {
  customElements.define('emberk-variant-pricing', EmberkVariantPricing);
}
